/**
 * Record & Review — the Review document the server returns
 * (GET /practice/sessions/:id/review, GET /human-coaching/shared-sessions/:id/review).
 *
 * Every media value carries an evidence class (docs/MEDIA-BOUNDARY.md §4). An
 * UNAVAILABLE value has NO `value` field: it is rendered as "Not measured — …",
 * never as a number, a zero, or a dead player.
 */

export type EvidenceClass = 'MEASURED' | 'DERIVED' | 'MODEL_INFERRED' | 'RUBRIC_INTERPRETED' | 'UNAVAILABLE';

export interface UnavailableEvidence {
  available: false;
  evidenceClass: 'UNAVAILABLE';
  reason: string;
  awaitingStage: string | null;
}

export interface MediaProvenance {
  adapter: string;
  producer: string;
  provider: string | null;
  serverVerified: boolean;
}

export type Evidence<T, C extends EvidenceClass> =
  | { available: true; evidenceClass: C; value: T; provenance: MediaProvenance }
  | UnavailableEvidence;

export interface ReviewSegment {
  index: number;
  text: string;
  /** Measured start/end in ms, or null when no word timings exist (never estimated). */
  startMs: number | null;
  endMs: number | null;
  fingerprint: string;
}

export type ReviewMetric =
  | {
      key: string;
      label: string;
      unit: 'score' | 'count' | 'seconds' | 'wpm';
      evidenceClass: Exclude<EvidenceClass, 'UNAVAILABLE'>;
      value: number;
      serverVerified?: boolean;
    }
  | {
      key: string;
      label: string;
      unit: 'score' | 'count' | 'seconds' | 'wpm';
      evidenceClass: 'UNAVAILABLE';
      reason: string;
      awaitingStage: string | null;
    };

export interface ReviewAnnotation {
  id: string;
  segmentIndex: number;
  attached: boolean;
  body: string;
  authoredByViewer: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PracticeReview {
  session: {
    id: string;
    type: string;
    status: string;
    startedAt: string | null;
    endedAt: string | null;
    scoringVersion: string | null;
  };
  viewer: { role: 'owner' | 'coach'; canAnnotate: boolean };
  recording: Evidence<{ playbackUrl: string; durationMs: number | null }, 'MEASURED'>;
  transcript: Evidence<{ text: string }, 'MODEL_INFERRED'>;
  timeline: {
    timing: 'MEASURED' | 'UNAVAILABLE';
    timingReason: string | null;
    timingAwaitingStage: string | null;
    segments: ReviewSegment[];
  };
  metrics: ReviewMetric[];
  annotations: { enabled: boolean; items: ReviewAnnotation[] };
  mediaAdapter: string;
}

export const ANNOTATION_BODY_MAX = 2000;

/** Plain-language reasons for unavailable evidence. */
export const UNAVAILABLE_REASON_TEXT: Record<string, string> = {
  recording_not_retained: 'practice recordings are not kept, so there is nothing to play back',
  word_timings_not_retained: 'word timings were not kept, so the segments have no times',
  word_timings_empty: 'no word timings were recorded',
  no_transcript: 'no transcript was produced for this session',
  duration_unmeasured: 'no speaking duration was measured',
  rubric_interpreted_only: 'confidence is a coaching judgement, not something measured from audio',
  acoustic_measurement_unavailable: 'vocal energy needs acoustic measurement, which is not available yet',
  pre_r0_overall_not_trustworthy: 'this older score included values that were never measured',
  no_audio_evidence: 'no audio evidence was analysed',
  modality_not_present: 'this session had no audio',
  insufficient_evidence: 'there was not enough speech to measure it',
  no_words: 'no words were counted',
  not_measured: 'it was not measured',
};

export function reasonText(reason: string): string {
  return UNAVAILABLE_REASON_TEXT[reason] ?? 'it was not measured';
}

export const EVIDENCE_CLASS_LABEL: Record<EvidenceClass, string> = {
  MEASURED: 'Measured',
  DERIVED: 'Derived',
  MODEL_INFERRED: 'Model-inferred',
  RUBRIC_INTERPRETED: 'Rubric judgement',
  UNAVAILABLE: 'Not measured',
};
